'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { EmbeddedTweet, useTweet } from 'react-tweet';
import { ArchivedTweetCard } from './ArchivedTweetCard';
import { AlertCircle, ExternalLink } from 'lucide-react';
import { ErrorBoundary } from '@/components/utils/ErrorBoundary';

// The syndication API returns tweets with `entities: {}` (no subarrays) when
// the tweet contains no hashtags/mentions/urls/symbols. react-tweet's
// enrichTweet() then iterates undefined and throws "x is not iterable",
// taking down the entire <ReactMarkdown> subtree. Normalising entities to
// empty arrays before they reach enrichTweet() fixes the crash at its source.
function sanitizeTweet(tweet: any): any {
  if (!tweet) return tweet;
  const fix = (t: any) => {
    if (!t) return t;
    const e = t.entities || {};
    t.entities = {
      ...e,
      hashtags: Array.isArray(e.hashtags) ? e.hashtags : [],
      user_mentions: Array.isArray(e.user_mentions) ? e.user_mentions : [],
      urls: Array.isArray(e.urls) ? e.urls : [],
      symbols: Array.isArray(e.symbols) ? e.symbols : [],
      // media stays optional — react-tweet already guards `if (tweet.entities.media)`
    };
    return t;
  };
  fix(tweet);
  if (tweet.quoted_tweet) fix(tweet.quoted_tweet);
  return tweet;
}

interface TwitterEmbedProps {
  tweetUrl: string;
  caption?: string;
}

interface CachedTweet {
  id: string;
  text: string;
  username: string;
  displayName?: string;
  authorProfilePic?: string | null;
  createdAt?: string | null;
  media?: Array<{
    type: 'photo' | 'video' | 'gif';
    url: string;
    thumbnailUrl?: string;
  }>;
  metrics?: {
    likes: number;
    retweets: number;
    replies: number;
  };
  tweetUrl: string;
  isRetweet?: boolean;
  retweetedTweet?: any;
  quotedTweet?: any;
}

// Extract tweet ID from various Twitter/X URL formats
function extractTweetId(url: string): string | null {
  const match = url.match(/(?:twitter\.com|x\.com)\/[\w]+\/status\/(\d+)/);
  return match ? match[1] : null;
}

// Fallback component when tweet is unavailable
//
// reason:
//   'unavailable'  — react-tweet decided the tweet is gone (deleted / suspended).
//                    Show the amber "archived" banner on the cached card.
//   'render-error' — react-tweet threw during render (typically the
//                    enrichTweet() iterates-undefined-entities crash). The
//                    underlying tweet still works, so the cached card should
//                    NOT pretend it's stale.
const TweetNotFound: React.FC<{
  tweetUrl: string;
  cachedTweet?: CachedTweet | null;
  reason?: 'unavailable' | 'render-error';
}> = ({ tweetUrl, cachedTweet, reason = 'unavailable' }) => {
  // If we have cached data, show the archived tweet card
  if (cachedTweet) {
    return <ArchivedTweetCard tweet={cachedTweet} variant={reason === 'render-error' ? 'live' : 'archived'} />;
  }

  // Otherwise show unavailable message
  return (
    <div className="my-6 flex justify-center">
      <div
        className="w-full bg-gray-50 dark:bg-brand-dark-lighter/50 rounded-xl border border-gray-200 dark:border-neutral-800 p-6"
        style={{ maxWidth: '550px' }}
      >
        <div className="flex flex-col items-center justify-center text-center gap-3">
          <AlertCircle className="w-8 h-8 text-gray-400 dark:text-gray-500" />
          <div>
            <p className="text-gray-600 dark:text-gray-400 font-medium">
              This tweet is no longer available
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
              The original tweet may have been deleted or the account suspended.
            </p>
          </div>
          <a
            href={tweetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-blue-500 hover:text-blue-600 transition-colors mt-2"
          >
            <span>Try viewing on X</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
};

// Wraps react-tweet's useTweet + EmbeddedTweet pipeline with an entities
// sanitization step. Mirrors react-tweet's <Tweet> internally but inserts
// sanitizeTweet() between fetch and render so enrichTweet() can't blow up
// on tweets whose syndication payload returned `entities: {}`.
interface SafeTweetProps {
  id: string;
  notFound: React.ReactNode;
}
const SafeTweet: React.FC<SafeTweetProps> = ({ id, notFound }) => {
  const { data, error, isLoading } = useTweet(id);
  if (isLoading) return <TweetLoading />;
  if (error || !data) return <>{notFound}</>;
  return <EmbeddedTweet tweet={sanitizeTweet(data)} />;
};

// Loading placeholder with fixed height to prevent layout shift
const TweetLoading: React.FC = () => (
  <div className="my-6 flex justify-center">
    <div
      className="w-full bg-gray-50 dark:bg-brand-dark-lighter/50 rounded-xl border border-gray-200 dark:border-neutral-800 p-6 animate-pulse"
      style={{ maxWidth: '550px', minHeight: '200px' }}
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-gray-200 dark:bg-neutral-700 rounded-full" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 dark:bg-neutral-700 rounded w-1/3" />
          <div className="h-3 bg-gray-200 dark:bg-neutral-700 rounded w-1/4" />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <div className="h-4 bg-gray-200 dark:bg-neutral-700 rounded w-full" />
        <div className="h-4 bg-gray-200 dark:bg-neutral-700 rounded w-5/6" />
        <div className="h-4 bg-gray-200 dark:bg-neutral-700 rounded w-4/6" />
      </div>
    </div>
  </div>
);

export const TwitterEmbed: React.FC<TwitterEmbedProps> = React.memo(({ tweetUrl, caption }) => {
  const [cachedTweet, setCachedTweet] = useState<CachedTweet | null>(null);
  const [cacheChecked, setCacheChecked] = useState(false);

  const tweetId = extractTweetId(tweetUrl);

  // Fetch cached tweet data for fallback
  useEffect(() => {
    if (!tweetId) {
      setCacheChecked(true);
      return;
    }

    const fetchCachedTweet = async () => {
      try {
        const response = await fetch(`/api/tweets/${tweetId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.tweet) {
            setCachedTweet(data.tweet);
          }
        }
      } catch (error) {
        console.error('Failed to fetch cached tweet:', error);
      } finally {
        setCacheChecked(true);
      }
    };

    fetchCachedTweet();
  }, [tweetId]);

  // Handle invalid URL
  if (!tweetId) {
    return (
      <div className="my-6 flex justify-center">
        <div className="text-gray-500 dark:text-gray-400 text-sm">
          Invalid tweet URL
        </div>
      </div>
    );
  }

  // Safety net for any future render-time throw inside react-tweet that
  // sanitizeTweet() didn't anticipate. Falls back to the cached card with
  // no "archived" framing since the tweet itself is presumably still live.
  const TweetErrorFallback: React.FC = () => (
    <TweetNotFound
      tweetUrl={tweetUrl}
      cachedTweet={cacheChecked ? cachedTweet : null}
      reason="render-error"
    />
  );

  return (
    <div className="my-6 flex flex-col items-center tweet-container">
      <ErrorBoundary fallback={TweetErrorFallback}>
        <Suspense fallback={<TweetLoading />}>
          <SafeTweet
            id={tweetId}
            notFound={<TweetNotFound tweetUrl={tweetUrl} cachedTweet={cacheChecked ? cachedTweet : null} />}
          />
        </Suspense>
      </ErrorBoundary>
      {caption && caption !== 'Tweet' && caption !== 'tweet' && (
        <div className="text-center text-sm text-gray-600 dark:text-gray-400 italic mt-2">
          {caption}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.tweetUrl === nextProps.tweetUrl && prevProps.caption === nextProps.caption;
});

TwitterEmbed.displayName = 'TwitterEmbed';

interface YouTubeEmbedProps {
  videoId: string;
  caption?: string;
}

export const YouTubeEmbed: React.FC<YouTubeEmbedProps> = ({ videoId, caption }) => {
  return (
    <div className="my-6">
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          title={caption || 'YouTube video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute top-0 left-0 w-full h-full rounded-lg"
        />
      </div>
      {caption && caption !== 'YouTube' && (
        <div className="text-center text-sm text-gray-600 italic mt-2">
          {caption}
        </div>
      )}
    </div>
  );
};
